import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, FormControlLabel, Grid, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import CoreReportsService from '../../../services/CoreReportsService';
import { StatTile } from './ReportCharts';
import { count, rejectionRate } from './reportsFormat';
import useReport from './useReport';
import ReportState from './ReportState';

export default function CfeTab({ days, onFreshness }) {
  const params = useMemo(() => ({ days }), [days]);
  const report = useReport((p) => CoreReportsService.cfe(p), params);
  const data = report.data;
  const freshness = data?.freshness || null;
  useEffect(() => { onFreshness?.(freshness); }, [freshness, onFreshness]);
  const [onlyWithCfe, setOnlyWithCfe] = useState(true);

  const rows = (data?.companies || []).filter((c) => !onlyWithCfe || c.cfeEmitted > 0 || c.cfeRejected > 0);

  return (
    <ReportState report={report}>
      {data && (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}><StatTile label="CFE emitidos" value={count(data.totals.cfeEmitted)} /></Grid>
            <Grid item xs={12} sm={4}>
              <StatTile
                label="CFE rechazados" value={count(data.totals.cfeRejected)}
                hint={data.totals.cfeEmitted > 0 ? `${rejectionRate(data.totals.cfeEmitted, data.totals.cfeRejected).toFixed(1).replace('.', ',')} % de los emitidos` : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={4}><StatTile label="Ventas sin CFE" value={count(data.totals.salesWithoutCfe)} hint="Solo empresas que emiten CFE son comparables" /></Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 2 }}>
            “Ventas sin CFE” cuenta toda venta procesada sin comprobante asociado. En una empresa que no usa facturación electrónica son todas sus ventas,
            por eso la tabla muestra por defecto solo las empresas que emitieron algún CFE.
          </Alert>

          <FormControlLabel sx={{ mt: 1 }} control={<Switch checked={onlyWithCfe} onChange={(e) => setOnlyWithCfe(e.target.checked)} />} label="Solo empresas que emiten CFE" />

          <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Empresa</TableCell>
                  <TableCell align="right">Ventas</TableCell>
                  <TableCell align="right">CFE emitidos</TableCell>
                  <TableCell align="right">Rechazados</TableCell>
                  <TableCell align="right">% rechazo</TableCell>
                  <TableCell align="right">Ventas sin CFE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay empresas con CFE en este rango.</TableCell></TableRow>}
                {rows.map((c) => {
                  const rate = rejectionRate(c.cfeEmitted, c.cfeRejected);
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell>{c.name}</TableCell>
                      <TableCell align="right">{count(c.salesCount)}</TableCell>
                      <TableCell align="right">{count(c.cfeEmitted)}</TableCell>
                      <TableCell align="right">{count(c.cfeRejected)}</TableCell>
                      <TableCell align="right">
                        {rate === null ? '-' : <Chip size="small" color={rate >= 5 ? 'error' : rate > 0 ? 'warning' : 'success'} variant="outlined" label={`${rate.toFixed(1).replace('.', ',')} %`} />}
                      </TableCell>
                      <TableCell align="right">{count(c.salesWithoutCfe)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>Ordenadas de más a menos rechazos.</Typography>
        </Box>
      )}
    </ReportState>
  );
}
